import {
  Directive,
  ViewContainerRef,
  AfterViewInit,
  signal,
  Injector,
  model,
  input,
  computed,
  linkedSignal,
  effect,
} from '@angular/core';
import { DifferenceButtonComponent } from './difference.component';
import VectorLayer from 'ol/layer/Vector';
import { Feature } from 'ol';
import { Difference, Intersects } from 'src/app/shared/helpers/transformations';

@Directive({
  selector: '[differenceTool]',
  standalone: true,
})
export class DifferenceToolDirective implements AfterViewInit {
  readonly active = model<boolean>(false);
  differenceTargetLayers = input<Array<VectorLayer<any>>>([]);

  differenceTargets = linkedSignal<any>(() => {
    const targetLayers = this.differenceTargetLayers();

    return targetLayers.map((layer) => ({
      layer: layer,
      isSelected: false,
      name: layer.get('title'),
      layerID: layer.get('id'),
      isLandUse: layer.get('isLandUse'),
    }));
  });

  anySelected = computed(() => {
    const items = this.differenceTargets() as any [];

    return items.some(i => i.isSelected);
  })

  allSelected = computed(() => {
    const items = this.differenceTargets();
    const total = items.length;
    const selectedCount = items.filter((i) => i.isSelected).length;

    return selectedCount === total && total > 0;
  });

  indeterminate = computed(() => {
    const items = this.differenceTargets();
    const total = items.length;
    const selectedCount = items.filter((i) => i.isSelected).length;
    return selectedCount >= 2 && selectedCount < total;
  });

  shouldDifferenceAgainst = computed(() => {
    const rows = this.differenceTargets();

    return rows
      .filter((row) => row.isSelected)
      .map(({ layer }) => layer)
      .flatMap((lyr) => lyr.getSource().getFeatures());
  });

  toggleSelectAll(selected: boolean) {
    const items = this.differenceTargets();

    const updatedItems = items.map((item) => ({
      ...item,
      isSelected: selected,
    }));

    this.differenceTargets.set(updatedItems);
  }

  updateRow(layerID: number, payload: Partial<any>) {
    const items = this.differenceTargets();

    const index = items.findIndex((e) => e.layerID === layerID);

    if (index !== -1) {
      const updatedItem = { ...items[index], ...payload };

      const updatedItems = [
        ...items.slice(0, index),
        updatedItem,
        ...items.slice(index + 1),
      ];

      this.differenceTargets.set(updatedItems);
    }
  }

  constructor(private vcr: ViewContainerRef, private parentInjector: Injector) {
    effect(() => {

      const anySelected = this.anySelected();

      this.active.set(anySelected);

    });
  }

  ngAfterViewInit(): void {
    const childInjector = Injector.create({
      providers: [{ provide: DifferenceToolDirective, useValue: this }],
      parent: this.parentInjector,
    });

    this.vcr.createComponent(DifferenceButtonComponent, {
      injector: childInjector,
    });
  }

  execute(target: Feature<any>) {
    if (target == null) {
      return;
    }

    if(!this.active())
      return;

    const shouldDifferenceAgainst = this.shouldDifferenceAgainst();

    for (let index = 0; index < shouldDifferenceAgainst.length; index++) {
      const feature = shouldDifferenceAgainst[index];
      const intersects = Intersects(target, feature);

      if (!intersects) {
        continue;
      }

      const clippedFeature = Difference(target, feature);

      target.setGeometry(clippedFeature.getGeometry());
    }
  }

  toggle(): void {
    this.active.update((v) => !v);
  }
}
